/**
 * appAlert — imperative API for styled in-app alerts and toasts.
 *
 * Why this exists: React Native's `Alert.alert` renders the OS-native dialog
 * — generic Material AlertDialog on Android, system iOS alert on iOS — which
 * looks foreign next to the rest of the Coach Cedar UI. This module replaces
 * Alert.alert (for success + info patterns) with branded modals/toasts that
 * match the rest of the app (cedar palette, rounded corners, cedarLight CTAs).
 *
 * Design contract:
 *   • Module-level event bus. No React context, no provider — callers don't
 *     need to be inside a tree. Service files (androidPermissions.ts, etc.)
 *     can call appAlert.show() from anywhere.
 *   • Single host. The matching <AppAlertHost /> is mounted exactly once at
 *     the app root (app/_layout.tsx). It subscribes to events and renders
 *     the visible UI.
 *   • Early-startup queue. If an event fires before the host has mounted
 *     (e.g. from a top-of-tree effect), it is queued and flushed on first
 *     subscribe. The queue keeps at most one of each kind ("last write wins")
 *     so a burst of toasts at startup doesn't spam the screen.
 *   • Web pass-through. On web (legacy prototype per project CLAUDE.md) the
 *     styled UI is not built; calls forward to Alert.alert so behaviour is
 *     preserved. Native is the target platform for the styled experience.
 *
 * Surfaces:
 *   • appAlert.toast(msg)            — transient success/info confirmation
 *   • appAlert.show({...})           — modal: single-OK (info) or multi-button
 *   • appAlert.error(title, msg)     — modal with a red "error" header
 *   • appAlert.confirm({...})        — two-button confirmation (destructive-aware)
 *
 * The `buttons` array on AppAlertOptions mirrors React Native's Alert.alert
 * button shape (text + style + onPress), so migrating existing multi-button
 * Alert.alert calls is a near-mechanical 1:1 mapping.
 */
import { Alert, Platform } from 'react-native';

// ─── Public types ────────────────────────────────────────────────────────────

export type ToastVariant = 'success' | 'info';

/** Header treatment for modal alerts. 'error' renders a red header + icon. */
export type AlertVariant = 'info' | 'error';

export interface AppToastOptions {
  /** Visual treatment — defaults to 'success' (cedar-prime green). */
  variant?: ToastVariant;
  /** Auto-dismiss timeout in ms. Defaults to 2500. */
  durationMs?: number;
}

/**
 * A single modal button. Shape matches React Native's Alert.alert button so
 * existing `Alert.alert(t, m, buttons)` calls map across 1:1.
 *   • 'default'     — primary action (cedarLight, white text)
 *   • 'cancel'      — secondary / dismiss (cedarSubtle, cedarPrime text)
 *   • 'destructive' — dangerous action (error red, white text)
 */
export interface AppAlertButton {
  text: string;
  style?: 'default' | 'cancel' | 'destructive';
  onPress?: () => void;
}

export interface AppAlertOptions {
  title: string;
  message?: string;
  /** Header treatment. Defaults to 'info' (cedar-prime header). */
  variant?: AlertVariant;
  /**
   * Buttons to render. When omitted, a single OK button is shown (using
   * okLabel/onOk below) — preserves the original single-OK info behavior.
   * 1 button → full width, 2 → side by side, 3+ → stacked vertically.
   */
  buttons?: AppAlertButton[];
  /** Label for the implicit single OK button (when `buttons` is omitted). */
  okLabel?: string;
  /** Fires when the user taps the implicit OK button. */
  onOk?: () => void;
  /**
   * Fires when the modal is dismissed by backdrop tap or hardware back with
   * NO cancel button present. When a cancel button exists, dismissal routes
   * to that button's onPress instead (safe default for confirmations).
   */
  onDismiss?: () => void;
}

export interface AppConfirmOptions {
  title: string;
  message?: string;
  /** Confirm button label. Defaults to 'Confirm'. */
  confirmText?: string;
  /** Cancel button label. Defaults to 'Cancel'. */
  cancelText?: string;
  /** When true, the confirm button is styled destructive (red). */
  destructive?: boolean;
  /** Fires when the user confirms. */
  onConfirm: () => void;
  /** Fires when the user cancels (tap Cancel, backdrop, or hardware back). */
  onCancel?: () => void;
}

export interface AppAlertApi {
  /** Show a modal alert. Single OK by default, or a custom `buttons` array. */
  show: (options: AppAlertOptions) => void;
  /** Show a transient toast at the bottom of the screen. */
  toast: (message: string, options?: AppToastOptions) => void;
  /** Show an error modal (red header) with a single OK button. */
  error: (title: string, message?: string) => void;
  /** Show a two-button confirmation. Destructive-aware. */
  confirm: (options: AppConfirmOptions) => void;
  /** Programmatically dismiss the active modal alert. */
  dismissAlert: () => void;
  /** Programmatically dismiss the active toast. */
  dismissToast: () => void;
}

// ─── Internal event bus ──────────────────────────────────────────────────────

export type AppAlertEvent =
  | { kind: 'show'; options: AppAlertOptions }
  | { kind: 'toast'; message: string; options?: AppToastOptions }
  | { kind: 'dismissAlert' }
  | { kind: 'dismissToast' };

type Listener = (event: AppAlertEvent) => void;

const listeners = new Set<Listener>();

/**
 * Pre-host queue. While `listeners` is empty (host not yet mounted) any
 * emitted event is held here. On first subscribe the queue is replayed in
 * order, then cleared. We collapse to one event per kind so a startup burst
 * of toasts/alerts doesn't replay as 10 visible UIs in sequence.
 */
const queue = new Map<AppAlertEvent['kind'], AppAlertEvent>();

const emit = (event: AppAlertEvent): void => {
  if (listeners.size === 0) {
    queue.set(event.kind, event);
    return;
  }
  listeners.forEach((listener) => listener(event));
};

/**
 * Subscribe to alert events. Returns an unsubscribe function suitable for
 * useEffect cleanup. On the first subscribe after startup, any queued
 * events are replayed to the new listener.
 */
export const subscribeAppAlert = (listener: Listener): (() => void) => {
  const isFirst = listeners.size === 0;
  listeners.add(listener);
  if (isFirst && queue.size > 0) {
    const drained = Array.from(queue.values());
    queue.clear();
    drained.forEach((event) => listener(event));
  }
  return () => {
    listeners.delete(listener);
  };
};

// ─── Outlet registry ─────────────────────────────────────────────────────────

/**
 * iOS cannot present a <Modal> over an already-presented <Modal>. The app's
 * drawers are <Modal>s (BottomSheet) and the alert host is another root <Modal>,
 * so an alert fired while a drawer is open silently no-ops on iOS.
 *
 * The fix: a registry of "outlets". When a drawer opens it pushes its id; the
 * topmost-registered outlet is where the alert/toast should render (INSIDE that
 * drawer's Modal, as a plain View — see AppAlertOutlet). When nothing is
 * registered the active outlet is the root host. A stack (not a single slot)
 * so drawer-to-drawer transitions and any nested sheet resolve correctly.
 *
 * This registry is intentionally framework-agnostic (no React) — the hooks that
 * consume it live in components/native/modals/appAlertController.ts.
 */
export const ROOT_OUTLET_ID = 'root';

const outletStack: string[] = [];
const outletListeners = new Set<() => void>();

const notifyOutletChange = (): void => {
  outletListeners.forEach((listener) => listener());
};

/** Push an outlet as the topmost render target. Returns an unregister fn. */
export const registerAlertOutlet = (id: string): (() => void) => {
  outletStack.push(id);
  notifyOutletChange();
  return () => {
    const index = outletStack.lastIndexOf(id);
    if (index !== -1) outletStack.splice(index, 1);
    notifyOutletChange();
  };
};

/** The topmost registered outlet id, or ROOT_OUTLET_ID when none. */
export const getActiveOutletId = (): string =>
  outletStack.length > 0 ? outletStack[outletStack.length - 1] : ROOT_OUTLET_ID;

/** Subscribe to outlet stack changes (push/pop). Returns an unsubscribe fn. */
export const subscribeOutletChange = (listener: () => void): (() => void) => {
  outletListeners.add(listener);
  return () => {
    outletListeners.delete(listener);
  };
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Web pass-through. On web the styled host is not mounted; calls forward to
 * the platform's Alert.alert so existing behaviour is preserved. Native is
 * where the branded UI lives.
 */
const isWeb = Platform.OS === 'web';

export const appAlert: AppAlertApi = {
  show: (options) => {
    if (isWeb) {
      // Map our button array onto Alert.alert's identical button shape, or
      // fall back to a single OK button when none was provided.
      const webButtons =
        options.buttons && options.buttons.length > 0
          ? options.buttons.map((b) => ({
              text: b.text,
              style: b.style,
              onPress: b.onPress,
            }))
          : [{ text: options.okLabel ?? 'OK', onPress: options.onOk }];
      Alert.alert(options.title, options.message, webButtons);
      return;
    }
    emit({ kind: 'show', options });
  },
  toast: (message, options) => {
    if (isWeb) {
      // Web has no toast primitive in this codebase; the closest equivalent
      // is a brief alert. Acceptable for legacy web — native is the target.
      Alert.alert('', message);
      return;
    }
    emit({ kind: 'toast', message, options });
  },
  error: (title, message) => {
    appAlert.show({ title, message, variant: 'error' });
  },
  confirm: ({
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    destructive = false,
    onConfirm,
    onCancel,
  }) => {
    // Cancel first (renders left / on top), action second (right / bottom).
    // Header stays cedar (variant 'info'); the danger lives on the button so
    // a routine "Save changes?" doesn't get an alarming red header.
    appAlert.show({
      title,
      message,
      buttons: [
        { text: cancelText, style: 'cancel', onPress: onCancel },
        {
          text: confirmText,
          style: destructive ? 'destructive' : 'default',
          onPress: onConfirm,
        },
      ],
    });
  },
  dismissAlert: () => {
    if (isWeb) return;
    emit({ kind: 'dismissAlert' });
  },
  dismissToast: () => {
    if (isWeb) return;
    emit({ kind: 'dismissToast' });
  },
};
